import { css, jsx } from '@emotion/react';
import { Link } from '@zapier/design-system';
import { useUniversalNavContext } from '../../../universal-nav/hooks';
import { baseReset, boxSizing, listReset, } from '../../../utils/style-mixins/reset';
const listGridGap = '1px';
const listGridStyles = css `
  display: grid;
  grid-template-columns: 100%;
  row-gap: ${listGridGap};
`;
const linkStyles = css `
  display: block;
  // Increase hit state
  padding: 3px 0;
  cursor: pointer;
  color: var(--zds-gray-warm-9, #413735);
  text-decoration: none;
  &:hover,
  &:focus {
    color: var(--zds-ui-primary-strongest, #503ebd);
    outline: none;
  }
`;
const toggleStyles = css `
  ${baseReset};
  display: block;
  width: 100%;
  margin-bottom: ${listGridGap};
  ${linkStyles};
  text-decoration: underline;

  // Dynamically change content when the details
  // element is opened and closed.
  details:not([open]) &::before {
    content: 'Show more';
  }
  details[open] &::before {
    content: 'Show less';
  }
`;
const Styles = {
    root: css `
    ${boxSizing};
  `,
    title: css `
    ${baseReset};
    font: var(--zds-body-1);
    font-weight: 700;
    color: var(--zds-gray-warm-9, #413735);
    margin-bottom: 10px;
  `,
    links: css `
    ${boxSizing};
    font: var(--zds-body-1);
    ${listGridStyles};
    // Prevent users from accidentally selecting
    // text when interacting with details. I don't
    // see a need for them to be able to copy the
    // link text to their clipboards.
    user-select: none;
  `,
    linksList: css `
    ${listReset};
    ${listGridStyles};
  `,
    link: css `
    ${boxSizing};
    ${linkStyles};
  `,
    moreLinks: css `
    ${baseReset};
    position: relative;
  `,
    moreLinksInner: css `
    ${boxSizing};
    ${listGridStyles};
  `,
    moreLinksToggle: css `
    // Hides default icon on details element
    &::-webkit-details-marker {
      display: none;
    }
    ${toggleStyles};

    // When details is open, the faux toggle will render
    // (so that it renders at the bottom of the list),
    // and the summary will be hidden but render over top
    // of it so it's still clickable.
    details[open] > & {
      position: absolute;
      bottom: 0;
      opacity: 0;
    }
  `,
    moreLinksFauxToggle: css `
    ${toggleStyles};
  `,
};
const LinksList = (props) => {
    const { getFullyQualifiedURL } = useUniversalNavContext();
    const linkNodes = props.links.map((link) => {
        return (jsx("li", { key: link.text, "data-zds": true },
            jsx(Link, { css: Styles.link, color: null, href: getFullyQualifiedURL(link.href) }, link.text)));
    });
    return (jsx("ul", { css: Styles.linksList, "data-zds": true }, linkNodes));
};
const MoreLinks = (props) => {
    return (jsx("details", { css: Styles.moreLinks, open: props.isExpanded, "data-zds": true },
        jsx("summary", { "aria-label": "Toggle more links", css: Styles.moreLinksToggle, onClick: props.onToggleExpanded, "data-zds": true }),
        jsx("div", { css: Styles.moreLinksInner, "data-zds": true },
            jsx(LinksList, { links: props.links }),
            jsx("span", { "aria-hidden": true, css: Styles.moreLinksFauxToggle, "data-zds": true }))));
};
export const LinksSection = ({ links, onToggleExpanded, title, initiallyShownCount = 5, isExpanded = undefined, }) => {
    const moreLinks = links.length > initiallyShownCount && (jsx(MoreLinks, { links: links.slice(initiallyShownCount), isExpanded: isExpanded, onToggleExpanded: onToggleExpanded }));
    return (jsx("section", { css: Styles.root, "data-zds": true },
        jsx("h2", { css: Styles.title, "data-zds": true }, title),
        jsx("div", { css: Styles.links, "data-zds": true },
            jsx(LinksList, { links: links.slice(0, initiallyShownCount) }),
            moreLinks)));
};
//# sourceMappingURL=LinksSection.js.map
// @zapier/universal-layout@12.0.37 (fb08e5a7f)
